import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaCarAlt, FaTools, FaCalendarAlt, FaChartLine, FaFileExport,
  FaFilter, FaSearch, FaSortAmountDown, FaSortAmountUp, 
  FaFileExcel, FaFileCsv, FaChevronDown, FaFileUpload,
  FaShieldAlt, FaExclamationTriangle, FaCheckCircle,
  FaTimesCircle, FaClock, FaChevronUp, FaEye, FaHistory,
  FaRoad, FaGasPump, FaWrench, FaMoneyBillWave, FaInfo,
  FaEdit, FaPlus, FaDownload, FaTachometerAlt
} from 'react-icons/fa';
import moment from 'moment';
import * as XLSX from 'xlsx';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import VehicleDetailsModal from '../components/VehicleManagement/VehicleDetailsModal';
import InsuranceImportModal from '../components/AdminDashBoard/Modals/InsuranceImportModal';
import { cars } from '../services/api';
import { toast } from 'react-toastify';

const VehicleServiceReport = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('last30');
  const [dateRange, setDateRange] = useState({
    start: moment().subtract(30, 'days').toDate(),
    end: moment().toDate()
  });
  const [sortField, setSortField] = useState('lastService');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [vehicleDetailsModalOpen, setVehicleDetailsModalOpen] = useState(false);
  const [insuranceImportModalOpen, setInsuranceImportModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedCar, setSelectedCar] = useState(null);
  const [expandedVehicles, setExpandedVehicles] = useState(new Set());
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  
  // Enhanced stats
  const [stats, setStats] = useState({
    totalVehicles: 0,
    vehiclesWithService: 0,
    totalServiceRecords: 0,
    totalServiceCost: 0,
    avgServiceCostPerVehicle: 0,
    vehiclesNeedingService: 0,
    // Insurance stats
    vehiclesWithValidInsurance: 0,
    insuranceExpiringSoon: 0,
    insuranceExpired: 0,
    totalInsuranceCost: 0,
    // Service urgency breakdown
    serviceOverdue: 0,
    serviceDueSoon: 0,
    serviceUpToDate: 0,
    noServiceHistory: 0
  });

  useEffect(() => {
    // Check for authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    fetchData();
  }, [navigate]);
  
  useEffect(() => {
    // Update date range when period changes
    switch (selectedPeriod) {
      case 'last30':
        setDateRange({
          start: moment().subtract(30, 'days').toDate(),
          end: moment().toDate()
        });
        break;
      case 'last90':
        setDateRange({
          start: moment().subtract(90, 'days').toDate(),
          end: moment().toDate()
        });
        break;
      case 'last180':
        setDateRange({
          start: moment().subtract(180, 'days').toDate(),
          end: moment().toDate()
        });
        break;
      case 'thisYear':
        setDateRange({
          start: moment().startOf('year').toDate(),
          end: moment().toDate()
        });
        break;
      case 'allTime':
        setDateRange({
          start: moment('2000-01-01').toDate(),
          end: moment().toDate()
        });
        break;
      default:
        break;
    }
  }, [selectedPeriod]);
  
  useEffect(() => {
    filterAndSortVehicles();
  }, [vehicles, searchQuery, filterStatus, dateRange, sortField, sortDirection]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const carsResponse = await cars.getAll();
      
      const allVehicles = [];
      let allServiceRecords = [];
      
      for (const car of carsResponse.data) {
        if (car.vehicleInstances && car.vehicleInstances.length > 0) {
          for (const vehicle of car.vehicleInstances) {
            // Calculate days since last service
            let lastServiceDate = null;
            let totalServiceCost = 0;
            
            if (vehicle.serviceHistory && vehicle.serviceHistory.length > 0) {
              const sortedHistory = [...vehicle.serviceHistory].sort(
                (a, b) => new Date(b.date) - new Date(a.date)
              );
              lastServiceDate = sortedHistory[0].date;
              totalServiceCost = vehicle.serviceHistory.reduce(
                (sum, record) => sum + (parseFloat(record.cost) || 0), 0
              );
              
              const vehicleServiceRecords = vehicle.serviceHistory.map(record => ({
                ...record,
                vehicleId: vehicle._id,
                carId: car._id,
                licensePlate: vehicle.licensePlate,
                carMake: car.make,
                carModel: car.model,
                carYear: car.year
              }));
              
              allServiceRecords = [...allServiceRecords, ...vehicleServiceRecords];
            }
            
            // Enhanced vehicle data with better categorization
            const daysSinceLastService = lastServiceDate 
              ? moment().diff(moment(lastServiceDate), 'days') 
              : null;
            
            let serviceStatus = 'unknown';
            if (!lastServiceDate) {
              serviceStatus = 'no-history';
            } else if (daysSinceLastService > 180) {
              serviceStatus = 'overdue';
            } else if (daysSinceLastService > 150) {
              serviceStatus = 'due-soon';
            } else {
              serviceStatus = 'up-to-date';
            }

            // Insurance status
            let insuranceStatus = 'unknown';
            let daysUntilInsuranceExpiry = null;
            if (vehicle.insurance && vehicle.insurance.endDate) {
              // Check if provider is TEMP (temporary/placeholder insurance)
              if (vehicle.insurance.provider && vehicle.insurance.provider.toUpperCase() === 'TEMP') {
                insuranceStatus = 'temp-needs-update';
              } else {
                daysUntilInsuranceExpiry = moment(vehicle.insurance.endDate).diff(moment(), 'days');
                if (daysUntilInsuranceExpiry < 0) {
                  insuranceStatus = 'expired';
                } else if (daysUntilInsuranceExpiry <= 30) {
                  insuranceStatus = 'expiring-soon';
                } else {
                  insuranceStatus = 'valid';
                }
              }
            } else {
              insuranceStatus = 'no-insurance';
            }
            
            allVehicles.push({
              _id: vehicle._id,
              carId: car._id,
              carMake: car.make,
              carModel: car.model,
              carYear: car.year,
              carCategory: car.category,
              vin: vehicle.vin,
              licensePlate: vehicle.licensePlate,
              status: vehicle.status,
              currentMileage: vehicle.currentMileage,
              purchaseDate: vehicle.purchaseDate,
              lastServiceDate,
              daysSinceLastService,
              serviceStatus,
              totalServiceCost,
              serviceRecordCount: vehicle.serviceHistory ? vehicle.serviceHistory.length : 0,
              insurance: vehicle.insurance,
              insuranceStatus,
              daysUntilInsuranceExpiry,
              insuranceValid: vehicle.insurance && new Date(vehicle.insurance.endDate) > new Date(),
              serviceHistory: vehicle.serviceHistory || [],
              kteo: vehicle.kteo,
              emissionCard: vehicle.emissionCard,
              gps: vehicle.gps,
              condition: vehicle.condition || 'good'
            });
          }
        }
      }
      
      setVehicles(allVehicles);
      setServiceRecords(allServiceRecords);
      calculateEnhancedStats(allVehicles, allServiceRecords);
      
      // Initial filtering
      filterAndSortVehicles();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load vehicle data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const calculateEnhancedStats = (vehiclesList, records) => {
    const totalVehicles = vehiclesList.length;
    
    // Service stats
    const vehiclesWithService = vehiclesList.filter(v => v.serviceRecordCount > 0).length;
    const totalServiceRecords = records.length;
    const totalServiceCost = records.reduce((sum, record) => sum + (parseFloat(record.cost) || 0), 0);
    const avgServiceCostPerVehicle = vehiclesWithService ? totalServiceCost / vehiclesWithService : 0;
    
    // Service status breakdown
    const serviceOverdue = vehiclesList.filter(v => v.serviceStatus === 'overdue').length;
    const serviceDueSoon = vehiclesList.filter(v => v.serviceStatus === 'due-soon').length;
    const serviceUpToDate = vehiclesList.filter(v => v.serviceStatus === 'up-to-date').length;
    const noServiceHistory = vehiclesList.filter(v => v.serviceStatus === 'no-history').length;
    const vehiclesNeedingService = serviceOverdue + serviceDueSoon;
    
    // Insurance stats
    const vehiclesWithValidInsurance = vehiclesList.filter(v => v.insuranceStatus === 'valid').length;
    const insuranceExpiringSoon = vehiclesList.filter(v => v.insuranceStatus === 'expiring-soon').length;
    const insuranceExpired = vehiclesList.filter(v => v.insuranceStatus === 'expired').length;
    const tempInsuranceNeedsUpdate = vehiclesList.filter(v => v.insuranceStatus === 'temp-needs-update').length;
    const totalInsuranceCost = vehiclesList.reduce((sum, v) => 
      sum + (v.insurance?.coverageAmount || 0), 0
    );
    
    setStats({
      totalVehicles,
      vehiclesWithService,
      totalServiceRecords,
      totalServiceCost,
      avgServiceCostPerVehicle,
      vehiclesNeedingService,
      vehiclesWithValidInsurance,
      insuranceExpiringSoon,
      insuranceExpired: insuranceExpired + tempInsuranceNeedsUpdate, // Combine expired and temp
      totalInsuranceCost,
      serviceOverdue,
      serviceDueSoon,
      serviceUpToDate,
      noServiceHistory
    });
  };
  
  const filterAndSortVehicles = () => {
    if (!vehicles.length) return;
    
    let filtered = [...vehicles];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(vehicle => 
        vehicle.licensePlate.toLowerCase().includes(query) ||
        vehicle.vin.toLowerCase().includes(query) ||
        `${vehicle.carMake} ${vehicle.carModel}`.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      switch (filterStatus) {
        case 'needsService':
          filtered = filtered.filter(v => v.serviceStatus === 'overdue' || v.serviceStatus === 'due-soon');
          break;
        case 'serviceOverdue':
          filtered = filtered.filter(v => v.serviceStatus === 'overdue');
          break;
        case 'serviceDueSoon':
          filtered = filtered.filter(v => v.serviceStatus === 'due-soon');
          break;
        case 'serviceUpToDate':
          filtered = filtered.filter(v => v.serviceStatus === 'up-to-date');
          break;
        case 'noServiceHistory':
          filtered = filtered.filter(v => v.serviceStatus === 'no-history');
          break;
        case 'insuranceExpired':
          filtered = filtered.filter(v => v.insuranceStatus === 'expired' || v.insuranceStatus === 'temp-needs-update');
          break;
        case 'insuranceExpiring':
          filtered = filtered.filter(v => v.insuranceStatus === 'expiring-soon');
          break;
        case 'insuranceValid':
          filtered = filtered.filter(v => v.insuranceStatus === 'valid');
          break;
        case 'noInsurance':
          filtered = filtered.filter(v => v.insuranceStatus === 'no-insurance');
          break;
        case 'tempInsurance':
          filtered = filtered.filter(v => v.insuranceStatus === 'temp-needs-update');
          break;
        default:
          filtered = filtered.filter(vehicle => vehicle.status === filterStatus);
      }
    }
    
    // Sort results
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'licensePlate':
          comparison = a.licensePlate.localeCompare(b.licensePlate);
          break;
        case 'model':
          comparison = `${a.carMake} ${a.carModel}`.localeCompare(`${b.carMake} ${b.carModel}`);
          break;
        case 'lastService':
          if (!a.lastServiceDate && !b.lastServiceDate) {
            comparison = 0;
          } else if (!a.lastServiceDate) {
            comparison = 1;
          } else if (!b.lastServiceDate) {
            comparison = -1;
          } else {
            comparison = new Date(a.lastServiceDate) - new Date(b.lastServiceDate);
          }
          break;
        case 'mileage':
          comparison = (a.currentMileage || 0) - (b.currentMileage || 0);
          break;
        case 'insuranceExpiry':
          if (!a.insurance?.endDate && !b.insurance?.endDate) {
            comparison = 0;
          } else if (!a.insurance?.endDate) {
            comparison = 1;
          } else if (!b.insurance?.endDate) {
            comparison = -1;
          } else {
            comparison = new Date(a.insurance.endDate) - new Date(b.insurance.endDate);
          }
          break;
        case 'serviceUrgency':
          const urgencyOrder = { 'overdue': 0, 'due-soon': 1, 'up-to-date': 2, 'no-history': 3 };
          comparison = urgencyOrder[a.serviceStatus] - urgencyOrder[b.serviceStatus];
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredVehicles(filtered);
  };

  const toggleVehicleExpansion = (vehicleId) => {
    const newExpanded = new Set(expandedVehicles);
    if (newExpanded.has(vehicleId)) {
      newExpanded.delete(vehicleId);
    } else {
      newExpanded.add(vehicleId);
    }
    setExpandedVehicles(newExpanded);
  };

  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'due-soon': return 'text-yellow-600 bg-yellow-100';
      case 'up-to-date': return 'text-green-600 bg-green-100';
      case 'no-history': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getInsuranceStatusColor = (status) => {
    switch (status) {
      case 'expired': return 'text-red-600 bg-red-100';
      case 'temp-needs-update': return 'text-red-600 bg-red-100';
      case 'expiring-soon': return 'text-yellow-600 bg-yellow-100';
      case 'valid': return 'text-green-600 bg-green-100';
      case 'no-insurance': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getServiceStatusIcon = (status) => {
    switch (status) {
      case 'overdue': return <FaTimesCircle className="text-red-500" />;
      case 'due-soon': return <FaExclamationTriangle className="text-yellow-500" />;
      case 'up-to-date': return <FaCheckCircle className="text-green-500" />;
      case 'no-history': return <FaClock className="text-gray-500" />;
      default: return <FaClock className="text-gray-500" />;
    }
  };

  const getInsuranceStatusIcon = (status) => {
    switch (status) {
      case 'expired': return <FaTimesCircle className="text-red-500" />;
      case 'temp-needs-update': return <FaExclamationTriangle className="text-red-500" />;
      case 'expiring-soon': return <FaExclamationTriangle className="text-yellow-500" />;
      case 'valid': return <FaShieldAlt className="text-green-500" />;
      case 'no-insurance': return <FaTimesCircle className="text-gray-500" />;
      default: return <FaShieldAlt className="text-gray-500" />;
    }
  };

  // Toggle export dropdown
  const toggleExportOptions = () => {
    setShowExportOptions(!showExportOptions);
  };
  
  // Function to handle opening the vehicle details modal
  const handleViewVehicleDetails = async (vehicleId, carId) => {
    try {
      // Fetch the car data to get vehicle context
      const carResponse = await cars.getById(carId);
      
      if (carResponse.data) {
        // Find the specific vehicle instance in the car's vehicleInstances array
        const vehicleInstance = carResponse.data.vehicleInstances.find(v => v._id === vehicleId);
        
        if (vehicleInstance) {
          // Set the selected vehicle and car data
          setSelectedVehicle(vehicleInstance);
          setSelectedCar(carResponse.data);
          setVehicleDetailsModalOpen(true);
        } else {
          console.error('Vehicle instance not found in car data');
        }
      }
    } catch (err) {
      console.error('Error fetching vehicle details:', err);
      // You could set an error state here if you want to show an error message
    }
  };
  
  // Toggle sorting when a column header is clicked
  const toggleSort = (field) => {
    if (sortField === field) {
      // If clicking on the same field that's already being sorted,
      // toggle the sort direction between ascending and descending
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking on a new field, set it as the sort field
      // and default to ascending order
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  const exportToExcel = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      ['VEHICLE SERVICE REPORT', '', '', '', '', '', '', '', '', '', '', '', ''],
      ['Generated on: ' + moment().format('YYYY-MM-DD HH:mm:ss'), '', '', '', '', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', '', '', '', ''],
      [
        'License Plate', 'VIN', 'Make', 'Model', 'Year', 
        'Status', 'Mileage', 'Last Service Date', 
        'Days Since Service', 'Insurance Provider', 
        'Insurance Expiry', 'Total Records', 'Total Cost (€)'
      ]
    ]);
    
    // Add data rows
    filteredVehicles.forEach(vehicle => {
      // Calculate total service cost
      const totalCost = vehicle.serviceHistory.reduce(
        (sum, record) => sum + (parseFloat(record.cost) || 0), 0
      );
      
      // Service status indicator
      let serviceStatus = '';
      if (!vehicle.lastServiceDate) {
        serviceStatus = 'No service history';
      } else if (vehicle.daysSinceLastService > 180) {
        serviceStatus = 'Overdue';
      } else if (vehicle.daysSinceLastService > 150) {
        serviceStatus = 'Due soon';
      } else {
        serviceStatus = 'Up to date';
      }
      
      XLSX.utils.sheet_add_aoa(worksheet, [[
        vehicle.licensePlate,
        vehicle.vin,
        vehicle.carMake,
        vehicle.carModel,
        vehicle.carYear,
        vehicle.status,
        vehicle.currentMileage ? `${vehicle.currentMileage} km` : 'N/A',
        vehicle.lastServiceDate ? moment(vehicle.lastServiceDate).format('YYYY-MM-DD') : 'Never',
        vehicle.daysSinceLastService ? `${vehicle.daysSinceLastService} days (${serviceStatus})` : 'N/A',
        vehicle.insurance?.provider || 'N/A',
        vehicle.insurance?.endDate ? moment(vehicle.insurance.endDate).format('YYYY-MM-DD') : 'N/A',
        vehicle.serviceHistory.length,
        `€${totalCost.toFixed(2)}`
      ]], { origin: -1 });
    });
    
    // Set column widths
    const columnWidths = [
      { wch: 15 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 8 },
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 18 },
      { wch: 15 }, { wch: 13 }, { wch: 15 }
    ];
    worksheet['!cols'] = columnWidths;
    
    // Define merged cells for title
    worksheet['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 12 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 12 } }
    ];
    
    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Vehicle Service Report');
    
    // Write to file and trigger download
    XLSX.writeFile(workbook, `vehicle-service-report-${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center p-8 bg-red-50 rounded-lg border border-red-200">
            <h3 className="text-xl text-red-700 mb-2">Error Loading Data</h3>
            <p className="text-gray-700">{error}</p>
            <button 
              onClick={() => fetchData()} 
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8 flex-grow">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <FaTools className="mr-3 text-blue-600" /> Vehicle Fleet Management
              </h1>
              <p className="text-gray-600 mt-2">
                Comprehensive overview of vehicle service history, insurance status, and maintenance needs
              </p>
            </div>
            <div className="mt-4 lg:mt-0 flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Back to Dashboard
              </button>
              
              <div className="relative">
                <button
                  onClick={toggleExportOptions}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center hover:bg-green-700 transition-colors"
                >
                  <FaFileExport className="mr-2" /> Export Report
                  <FaChevronDown className="ml-2" size={12} />
                </button>
                
                {showExportOptions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          exportToExcel();
                          setShowExportOptions(false);
                        }}
                        className="w-full px-4 py-2 text-left text-gray-700 hover:bg-green-50 flex items-center"
                      >
                        <FaFileExcel className="mr-2 text-green-600" /> Excel Format (.xlsx)
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setInsuranceImportModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center hover:bg-blue-700 transition-colors"
              >
                <FaFileUpload className="mr-2" /> Import Insurance
              </button>
            </div>
          </div>

          {/* Enhanced Stats Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
            {/* Fleet Overview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-blue-700 text-sm font-semibold">Total Fleet</h3>
                <span className="p-2 bg-blue-200 rounded-lg">
                  <FaCarAlt className="text-blue-700 text-lg" />
                </span>
              </div>
              <div className="text-2xl font-bold text-blue-800">{stats.totalVehicles}</div>
              <div className="text-xs text-blue-600 mt-1">
                {stats.vehiclesWithService} with service records
              </div>
            </motion.div>

            {/* Service Status Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-red-700 text-sm font-semibold">Service Overdue</h3>
                <span className="p-2 bg-red-200 rounded-lg">
                  <FaExclamationTriangle className="text-red-700 text-lg" />
                </span>
              </div>
              <div className="text-2xl font-bold text-red-800">{stats.serviceOverdue}</div>
              <div className="text-xs text-red-600 mt-1">
                180+ days since service
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-yellow-700 text-sm font-semibold">Service Due Soon</h3>
                <span className="p-2 bg-yellow-200 rounded-lg">
                  <FaClock className="text-yellow-700 text-lg" />
                </span>
              </div>
              <div className="text-2xl font-bold text-yellow-800">{stats.serviceDueSoon}</div>
              <div className="text-xs text-yellow-600 mt-1">
                150+ days since service
              </div>
            </motion.div>

            {/* Insurance Status Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-red-700 text-sm font-semibold">Insurance Expired</h3>
                <span className="p-2 bg-red-200 rounded-lg">
                  <FaTimesCircle className="text-red-700 text-lg" />
                </span>
              </div>
              <div className="text-2xl font-bold text-red-800">{stats.insuranceExpired}</div>
              <div className="text-xs text-red-600 mt-1">
                Immediate attention needed
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-yellow-700 text-sm font-semibold">Insurance Expiring</h3>
                <span className="p-2 bg-yellow-200 rounded-lg">
                  <FaShieldAlt className="text-yellow-700 text-lg" />
                </span>
              </div>
              <div className="text-2xl font-bold text-yellow-800">{stats.insuranceExpiringSoon}</div>
              <div className="text-xs text-yellow-600 mt-1">
                Within 30 days
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-green-700 text-sm font-semibold">Total Service Cost</h3>
                <span className="p-2 bg-green-200 rounded-lg">
                  <FaMoneyBillWave className="text-green-700 text-lg" />
                </span>
              </div>
              <div className="text-2xl font-bold text-green-800">€{stats.totalServiceCost.toFixed(0)}</div>
              <div className="text-xs text-green-600 mt-1">
                {stats.totalServiceRecords} service records
              </div>
            </motion.div>
          </div>

          {/* Enhanced Filters */}
          <div className="bg-gray-50 p-6 rounded-xl mb-6">
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="lg:w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Vehicles</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="License plate, VIN, or model..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <FaSearch className="absolute left-3 top-4 text-gray-400" />
                </div>
              </div>
              
              <div className="lg:w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Vehicles</option>
                  <optgroup label="Service Status">
                    <option value="serviceOverdue">Service Overdue (180+ days)</option>
                    <option value="serviceDueSoon">Service Due Soon (150+ days)</option>
                    <option value="serviceUpToDate">Service Up to Date</option>
                    <option value="noServiceHistory">No Service History</option>
                  </optgroup>
                  <optgroup label="Insurance Status">
                    <option value="insuranceExpired">Insurance Expired/Invalid</option>
                    <option value="tempInsurance">Temporary Insurance (TEMP)</option>
                    <option value="insuranceExpiring">Insurance Expiring (30 days)</option>
                    <option value="insuranceValid">Insurance Valid</option>
                    <option value="noInsurance">No Insurance</option>
                  </optgroup>
                  <optgroup label="Vehicle Status">
                    <option value="available">Available</option>
                    <option value="rented">Rented</option>
                    <option value="maintenance">In Maintenance</option>
                    <option value="unavailable">Unavailable</option>
                  </optgroup>
                </select>
              </div>
              
              <div className="lg:w-1/3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="licensePlate">License Plate</option>
                  <option value="model">Vehicle Model</option>
                  <option value="lastService">Last Service Date</option>
                  <option value="mileage">Mileage</option>
                  <option value="insuranceExpiry">Insurance Expiry</option>
                  <option value="serviceUrgency">Service Urgency</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              {/* <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  Showing {filteredVehicles.length} of {vehicles.length} vehicles
                </span>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800"
                >
                  {sortDirection === 'asc' ? <FaSortAmountUp className="mr-1" /> : <FaSortAmountDown className="mr-1" />}
                  {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                </button>
              </div> */}
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">View:</span>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-1 rounded ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Cards
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-1 rounded ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  Table
                </button>
              </div>
            </div>
          </div>

          {/* Vehicle Display */}
          {viewMode === 'cards' ? (
            /* Enhanced Card View */
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              <AnimatePresence>
                {filteredVehicles.map(vehicle => (
                  <motion.div
                    key={vehicle._id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                  >
                    {/* Card Header */}
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-lg text-gray-800">
                          {vehicle.licensePlate}
                        </h3>
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          vehicle.status === 'available' ? 'bg-green-100 text-green-800' :
                          vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                          vehicle.status === 'rented' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                        </span>
                      </div>
                      <p className="text-gray-600 text-sm">
                        {vehicle.carMake} {vehicle.carModel} ({vehicle.carYear})
                      </p>
                      <p className="text-xs text-gray-500 mt-1">VIN: {vehicle.vin}</p>
                    </div>

                    {/* Quick Stats */}
                    <div className="p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center space-x-2">
                          <FaTachometerAlt className="text-blue-500 text-sm" />
                          <div>
                            <p className="text-xs text-gray-500">Mileage</p>
                            <p className="text-sm font-medium">
                              {vehicle.currentMileage ? `${vehicle.currentMileage.toLocaleString()} km` : 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <FaHistory className="text-green-500 text-sm" />
                          <div>
                            <p className="text-xs text-gray-500">Service Records</p>
                            <p className="text-sm font-medium">{vehicle.serviceRecordCount}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status Indicators */}
                    <div className="p-4 space-y-3">
                      {/* Service Status */}
                      <div className="flex items-center space-x-3">
                        {getServiceStatusIcon(vehicle.serviceStatus)}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Service Status</span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getServiceStatusColor(vehicle.serviceStatus)}`}>
                              {vehicle.serviceStatus === 'overdue' && 'Overdue'}
                              {vehicle.serviceStatus === 'due-soon' && 'Due Soon'}
                              {vehicle.serviceStatus === 'up-to-date' && 'Up to Date'}
                              {vehicle.serviceStatus === 'no-history' && 'No History'}
                            </span>
                          </div>
                          {vehicle.lastServiceDate && (
                            <p className="text-xs text-gray-500 mt-1">
                              Last service: {moment(vehicle.lastServiceDate).format('DD/MM/YYYY')} 
                              ({vehicle.daysSinceLastService} days ago)
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Insurance Status */}
                      <div className="flex items-center space-x-3">
                        {getInsuranceStatusIcon(vehicle.insuranceStatus)}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Insurance Status</span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getInsuranceStatusColor(vehicle.insuranceStatus)}`}>
                              {vehicle.insuranceStatus === 'expired' && 'Expired'}
                              {vehicle.insuranceStatus === 'temp-needs-update' && 'TEMP - Needs Update'}
                              {vehicle.insuranceStatus === 'expiring-soon' && 'Expiring Soon'}
                              {vehicle.insuranceStatus === 'valid' && 'Valid'}
                              {vehicle.insuranceStatus === 'no-insurance' && 'No Insurance'}
                            </span>
                          </div>
                          {vehicle.insurance && (
                            <p className="text-xs text-gray-500 mt-1">
                              {vehicle.insurance.provider} - 
                              {vehicle.insuranceStatus === 'expired' 
                                ? ` Expired ${Math.abs(vehicle.daysUntilInsuranceExpiry)} days ago`
                                : vehicle.insuranceStatus === 'temp-needs-update'
                                ? ` Temporary insurance - Update required`
                                : vehicle.insuranceStatus === 'expiring-soon'
                                ? ` Expires in ${vehicle.daysUntilInsuranceExpiry} days`
                                : ` Valid until ${moment(vehicle.insurance.endDate).format('DD/MM/YYYY')}`
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expandable Details */}
                    <div className="border-t border-gray-100">
                      <button
                        onClick={() => toggleVehicleExpansion(vehicle._id)}
                        className="w-full p-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                      >
                        <span className="text-sm font-medium text-gray-700">
                          {expandedVehicles.has(vehicle._id) ? 'Hide Details' : 'Show Details'}
                        </span>
                        {expandedVehicles.has(vehicle._id) ? 
                          <FaChevronUp className="text-gray-400" /> : 
                          <FaChevronDown className="text-gray-400" />
                        }
                      </button>
                      
                      <AnimatePresence>
                        {expandedVehicles.has(vehicle._id) && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-t border-gray-100 p-4 bg-gray-50"
                          >
                            {/* Recent Service History */}
                            {vehicle.serviceHistory.length > 0 && (
                              <div className="mb-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                  <FaHistory className="mr-2" /> Recent Services
                                </h4>
                                <div className="space-y-2">
                                  {vehicle.serviceHistory
                                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                                    .slice(0, 3)
                                    .map((service, index) => (
                                      <div key={index} className="bg-white p-2 rounded border text-xs">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <p className="font-medium">{service.type}</p>
                                            <p className="text-gray-500">{service.description}</p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-gray-600">{moment(service.date).format('DD/MM/YY')}</p>
                                            {service.cost && <p className="text-green-600">€{parseFloat(service.cost).toFixed(2)}</p>}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  }
                                </div>
                              </div>
                            )}

                            {/* Additional Info */}
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              {vehicle.kteo && (
                                <div>
                                  <p className="font-medium text-gray-700">KTEO</p>
                                  <p className={`${
                                    moment(vehicle.kteo.expiryDate).isBefore(moment()) ? 'text-red-600' : 'text-green-600'
                                  }`}>
                                    {moment(vehicle.kteo.expiryDate).format('DD/MM/YYYY')}
                                  </p>
                                </div>
                              )}
                              {vehicle.condition && (
                                <div>
                                  <p className="font-medium text-gray-700">Condition</p>
                                  <p className="capitalize">{vehicle.condition}</p>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-4 flex space-x-2">
                              <button
                                onClick={() => handleViewVehicleDetails(vehicle._id, vehicle.carId)}
                                className="flex-1 px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors flex items-center justify-center"
                              >
                                <FaEye className="mr-1" /> Full Details
                              </button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            /* Enhanced Table View */
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-4 px-6 text-left">
                        <button
                          onClick={() => toggleSort('licensePlate')}
                          className="flex items-center font-semibold text-gray-700 hover:text-gray-900"
                        >
                          Vehicle
                          {sortField === 'licensePlate' && (
                            sortDirection === 'asc' ? <FaSortAmountUp className="ml-1" /> : <FaSortAmountDown className="ml-1" />
                          )}
                        </button>
                      </th>
                      <th className="py-4 px-6 text-left">
                        <button
                          onClick={() => toggleSort('serviceUrgency')}
                          className="flex items-center font-semibold text-gray-700 hover:text-gray-900"
                        >
                          Service Status
                          {sortField === 'serviceUrgency' && (
                            sortDirection === 'asc' ? <FaSortAmountUp className="ml-1" /> : <FaSortAmountDown className="ml-1" />
                          )}
                        </button>
                      </th>
                      <th className="py-4 px-6 text-left">
                        <button
                          onClick={() => toggleSort('insuranceExpiry')}
                          className="flex items-center font-semibold text-gray-700 hover:text-gray-900"
                        >
                          Insurance Status
                          {sortField === 'insuranceExpiry' && (
                            sortDirection === 'asc' ? <FaSortAmountUp className="ml-1" /> : <FaSortAmountDown className="ml-1" />
                          )}
                        </button>
                      </th>
                      <th className="py-4 px-6 text-left">
                        <button
                          onClick={() => toggleSort('mileage')}
                          className="flex items-center font-semibold text-gray-700 hover:text-gray-900"
                        >
                          Mileage
                          {sortField === 'mileage' && (
                            sortDirection === 'asc' ? <FaSortAmountUp className="ml-1" /> : <FaSortAmountDown className="ml-1" />
                          )}
                        </button>
                      </th>
                      <th className="py-4 px-6 text-left font-semibold text-gray-700">Status</th>
                      <th className="py-4 px-6 text-left font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredVehicles.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-12 text-center text-gray-500">
                          <FaCarAlt className="mx-auto text-4xl mb-4 text-gray-300" />
                          <p className="text-lg">No vehicles match the current filters</p>
                          <p className="text-sm">Try adjusting your search criteria</p>
                        </td>
                      </tr>
                    ) : (
                      filteredVehicles.map(vehicle => (
                        <tr key={vehicle._id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-6">
                            <div>
                              <p className="font-semibold text-gray-900">{vehicle.licensePlate}</p>
                              <p className="text-sm text-gray-600">
                                {vehicle.carMake} {vehicle.carModel} ({vehicle.carYear})
                              </p>
                              <p className="text-xs text-gray-500">{vehicle.vin}</p>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center space-x-2">
                              {getServiceStatusIcon(vehicle.serviceStatus)}
                              <div>
                                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getServiceStatusColor(vehicle.serviceStatus)}`}>
                                  {vehicle.serviceStatus === 'overdue' && 'Overdue'}
                                  {vehicle.serviceStatus === 'due-soon' && 'Due Soon'}
                                  {vehicle.serviceStatus === 'up-to-date' && 'Up to Date'}
                                  {vehicle.serviceStatus === 'no-history' && 'No History'}
                                </span>
                                {vehicle.lastServiceDate && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {vehicle.daysSinceLastService} days ago
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center space-x-2">
                              {getInsuranceStatusIcon(vehicle.insuranceStatus)}
                              <div>
                                <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getInsuranceStatusColor(vehicle.insuranceStatus)}`}>
                                  {vehicle.insuranceStatus === 'expired' && 'Expired'}
                                  {vehicle.insuranceStatus === 'temp-needs-update' && 'TEMP - Needs Update'}
                                  {vehicle.insuranceStatus === 'expiring-soon' && 'Expiring Soon'}
                                  {vehicle.insuranceStatus === 'valid' && 'Valid'}
                                  {vehicle.insuranceStatus === 'no-insurance' && 'No Insurance'}
                                </span>
                                {vehicle.insurance && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {vehicle.insurance.provider}
                                    {vehicle.insuranceStatus === 'temp-needs-update' && ' (Update Required)'}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center space-x-2">
                              <FaTachometerAlt className="text-blue-500 text-sm" />
                              <span className="text-sm font-medium">
                                {vehicle.currentMileage ? `${vehicle.currentMileage.toLocaleString()} km` : 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex px-3 py-1 text-xs rounded-full ${
                              vehicle.status === 'available' ? 'bg-green-100 text-green-800' :
                              vehicle.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                              vehicle.status === 'rented' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)}
                            </span>
                          </td>
                          <td className="py-4 px-6">
                            <button
                              onClick={() => handleViewVehicleDetails(vehicle._id, vehicle.carId)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                            >
                              <FaEye className="mr-1" /> View Details
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>
      </div>
      
      <Footer />

      {/* Modals */}
      {selectedVehicle && selectedCar && (
        <VehicleDetailsModal
          vehicle={selectedVehicle}
          car={selectedCar}
          isOpen={vehicleDetailsModalOpen}
          onClose={() => {
            setVehicleDetailsModalOpen(false);
            setSelectedVehicle(null);
            setSelectedCar(null);
            fetchData();
          }}
          onUpdate={(type, action) => {
            fetchData();
          }}
        />
      )}

      <InsuranceImportModal
        isOpen={insuranceImportModalOpen}
        onClose={() => setInsuranceImportModalOpen(false)}
        onSuccess={(results) => {
          fetchData();
        }}
      />
    </div>
  );
};

export default VehicleServiceReport;
